// tslint:disable max-func-body-length
import * as React from 'react';
import { ElementLoader, PromptConfirmation, RadioSymbol } from 'ui';
import { ModalWrapper } from 'app/components/modal-wrapper/modal-wrapper';
import { replaceTemplateString } from 'app/infrastructure/platform';
import {
  emptyUserGroups, hasFieldValueChanged, isDisabled
} from 'app/area/favourite-administration/list/change-user-groups/change-user-goups-helpers';
import { ChangeOptionType } from 'app/area/favourite-administration/list/types';
import { ChangeUserGroupsComponentProps, ChangeUserGroupsField } from 'app/area/favourite-administration/list/change-user-groups/types';
import { FavouriteListIds } from 'app/area/favourite-administration/list/constants';
import { ComplexValue, LoadOptions } from 'app/types-shared';
import { endpoints } from 'app/area/favourite-administration/list/endpoints';
import { CodeMultipleHierarchyPicker } from 'app/area/codetable/common';
import { FavouriteFormIds } from 'app/area/favourite-administration/form/constants';
import { manageOptionsUserGroup } from 'app/area/shared/manage-options';
import { ApplyOnDescendants } from 'app/area/favourite-administration/list/components/apply-on-descendants';
import styles from './change-user-goups.scss';

interface ChangeUserGroupsComponentState {
  shouldDisplayModal: boolean;
}

export class ChangeUserGroupsModal extends React.PureComponent<ChangeUserGroupsComponentProps, ChangeUserGroupsComponentState> {

  constructor(props: ChangeUserGroupsComponentProps) {
    super(props);

    this.state = {
      shouldDisplayModal: undefined
    };
  }

  public componentWillUnmount(): void {
    this.props.clearChangeUserGroups();
  }

  public componentDidUpdate(prevProps: ChangeUserGroupsComponentProps) {
    this.handleIsDirty(prevProps);
  }

  private handleIsDirty = (prevProps: ChangeUserGroupsComponentProps) => {
    hasFieldValueChanged(prevProps, this.props) && this.props.updateIsDirty(true);
  };

  private toggleModal = () => {
    this.setState({ shouldDisplayModal: !this.state.shouldDisplayModal });
  };

  private continue = () => {
    this.toggleModal();
    this.props.save();
    this.props.updateIsDirty(false);
  };

  private onClose = () => {
    this.props.onClose();
    this.props.updateIsDirty(false);
  };

  private addUserGroups = () => {
    this.props.updateByKey(ChangeUserGroupsField.replaceExistingUserGroups, undefined);
    this.props.resetValidation(FavouriteListIds.sharedWithUserGroups);
    this.props.updateState(ChangeOptionType.Add);
  };

  private replaceUserGroups = () => {
    this.props.updateByKey(ChangeUserGroupsField.replaceExistingUserGroups, undefined);
    this.props.resetValidation(FavouriteListIds.sharedWithUserGroups);
    this.props.updateState(ChangeOptionType.Replace);
  };

  private onChange = (values: ComplexValue<number>[]) => {
    this.props.resetValidation(FavouriteListIds.sharedWithUserGroups);
    this.props.updateByKey(ChangeUserGroupsField.replaceExistingUserGroups, values);
  };

  private onLoadOptions = (id: string, loadOptionsParams: LoadOptions) => {
    this.props.loadOptions(FavouriteListIds.sharedWithUserGroups, endpoints.sharedWithUserGroupOptions, loadOptionsParams);
  };

  private onApplyOnDescendants = (value: number) => {
    this.props.updateByKey(ChangeUserGroupsField.applyOnDescendants, !!value);
  };

  public render() {
    const { list: {
      changeUserGroups, fetchingChangeUserGroups, numberOfResults, editableNumberOfResults
    }, options, translation, validation } = this.props;

    const selectAddUserGroups = changeUserGroups.state === ChangeOptionType.Add;
    const selectReplaceUserGroups = changeUserGroups.state === ChangeOptionType.Replace;

    const extraFooterButton = manageOptionsUserGroup(translation.manage_options);

    const picker = <CodeMultipleHierarchyPicker
      id={FavouriteFormIds.userGroups}
      cols={12}
      className={'mb-1'}
      label={undefined}
      valueItems={changeUserGroups?.form?.[ChangeUserGroupsField.replaceExistingUserGroups] || []}
      validation={validation?.sharedWithUserGroups}
      locale={this.props.locale}
      readOnly={false}
      displaySelectAll={true}
      options={options?.[FavouriteListIds.sharedWithUserGroups]?.codes as ComplexValue<number>[] || []}
      fetchingOptions={options?.[FavouriteListIds.sharedWithUserGroups]?.fetching}
      translation={translation as any}
      extraFooterButton={extraFooterButton}
      onLoadOptions={this.onLoadOptions}
      onChange={this.onChange} />;

    return (<>
      <ModalWrapper
        headerTitle={`${translation.change_user_groups} (${numberOfResults})`}
        btnTitle={translation.save}
        btnCancelTitle={translation.cancel}
        onClose={this.onClose}
        onDone={this.toggleModal}
        closeOnDone={false}
        largeSize={false}
        isCancelDisabled={fetchingChangeUserGroups}
        isDisabled={fetchingChangeUserGroups || emptyUserGroups(this.props) || isDisabled(this.props)}
        canCloseOnBackground={!fetchingChangeUserGroups}
        canCloseOnXButton={!fetchingChangeUserGroups}>

        {fetchingChangeUserGroups
          ? <ElementLoader />
          : <div>
            <p className={styles.info}>{translation.shared_with_user_groups_info}</p>
            <div className="row">
              <div className="col-12">
                <div className={styles.container}>
                  <div className="form-check">
                    <input
                      id="add-user-groups"
                      type="radio"
                      className="form-check-input visuallyhidden"
                      value={ChangeOptionType.Add}
                      checked={selectAddUserGroups}
                      onChange={this.addUserGroups} />
                    <label
                      htmlFor="add-user-groups"
                      className="form-check-label">
                      <RadioSymbol />
                      <b>{translation.add_user_groups}</b>
                    </label>
                    {selectAddUserGroups ? picker : null}

                    <input
                      id="replace-user-groups"
                      type="radio"
                      className="form-check-input visuallyhidden"
                      value={ChangeOptionType.Replace}
                      checked={selectReplaceUserGroups}
                      onChange={this.replaceUserGroups} />
                    <label
                      htmlFor="replace-user-groups"
                      className="form-check-label">
                      <RadioSymbol />
                      <b>{translation.replace_existing_user_groups}</b>
                    </label>
                    {selectReplaceUserGroups ? picker : null}
                  </div>
                </div>
                <ApplyOnDescendants
                  value={changeUserGroups?.form?.[ChangeUserGroupsField.applyOnDescendants]}
                  onChange={this.onApplyOnDescendants}
                  translation={translation} />
              </div>
            </div>
          </div>}
      </ModalWrapper>

      {this.state.shouldDisplayModal && <PromptConfirmation
        title={translation.change_user_groups}
        description={replaceTemplateString(
          translation.change_user_groups_confirmation_description,
          editableNumberOfResults
        )}
        continueText={translation.change_user_groups_done}
        cancelText={translation.cancel}
        onContinue={this.continue}
        onCancel={this.toggleModal} />}
    </>);
  }
}
